import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { INFOMODULES, MODULES, t } from '../content/content'
import type { InfomoduleId } from '../content/content'
import { useKiosk } from '../state/store'
import { ProductsheetQR } from '../producten/ProductsheetQR'
import { FilmKnop, VEER } from './gedeeld'

/**
 * Stellingen en Tennant (feedback 21 oktober): twee onderwerpen naast de route
 * van de flowchart, als aanleiding voor het gesprek met sales. Geen keuze en
 * geen calculator, alleen een infoscherm: kop, intro, de punten, de filmknop
 * als er een film is en een QR naar meer informatie.
 *
 * Het podium heeft de opbouw van het hotspotpaneel in Automatisering, zodat de
 * zes onderdelen één familie blijven; de basis is het `AlleenFilm` dat met
 * Klantcases verviel (8feac52). De actiebalk is die van Connectiviteit: de
 * terugtegel en één teksttegel over de vier kolommen ernaast.
 */
export function Infomodule({ id, terug }: { id: InfomoduleId; terug: ReactNode }) {
  const taal = useKiosk((s) => s.taal)
  const module = MODULES.find((m) => m.id === id)!
  const info = INFOMODULES[id]

  return (
    <>
      <div className="kiosk-podium">
        <motion.div key={id} initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={VEER}>
          <div className="kiosk-tegel-nr">{module.nr}</div>
          <h2 className="kiosk-hero kiosk-hero-m">{t(info.kop, taal)}</h2>
          <p className="kiosk-onderkop">{t(info.intro, taal)}</p>
          <ul className="kiosk-voordelen">
            {info.punten.map((p, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...VEER, delay: 0.18 + i * 0.07 }}
              >
                {t(p, taal)}
              </motion.li>
            ))}
          </ul>
          {(info.video || info.qr) && (
            <motion.div
              className="kiosk-hotspot-acties"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...VEER, delay: 0.42 }}
            >
              {info.video && <FilmKnop video={info.video} />}
              {info.qr && <ProductsheetQR sheet={info.qr.sleutel} kop={t(info.qr.kop, taal)} taal={taal} compact />}
            </motion.div>
          )}
        </motion.div>
      </div>

      <div className="kiosk-actiebalk">
        {terug}
        <div className="kiosk-tegel kiosk-tegel-tekst">
          <span className="kiosk-tegel-nr">{module.nr}</span>
          <span className="kiosk-tegel-naam">{t(module.naam, taal)}</span>
          <span className="kiosk-tegel-sub">{t(module.sub, taal)}</span>
        </div>
      </div>
    </>
  )
}
